import { Card, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { CalendarIcon, PhoneIcon, MapPinIcon, Map, Trees } from "lucide-react"
import { getCommodityColor } from "@/utils/shared"

export interface FarmerCardProps {
    first_name: string;
    last_name: string;
    phone_number: string;
    passport_photo?: string;
    cluster_name: string;
    cooperative_name?: string;
    commodity: string;
    size_in_hectares: number;
    creation: string;
}

export const FarmerCard = ({ farmer }: { farmer: FarmerCardProps }) => {
    const dateObj = farmer.creation ? new Date(farmer.creation) : undefined;
    const formattedDate =
        dateObj && !isNaN(dateObj.getTime())
            ? dateObj.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
            : "Unknown";

    return (
        <Card className="cursor-pointer hover:shadow-md transition-all duration-300 w-80 overflow-hidden border-muted">
            {/* Commodity color indicator at top of card */}
            <div className={`h-1.5 w-full ${getCommodityColor(farmer.commodity)}`} />

            <CardHeader className="pb-2">
                <div className="flex items-center gap-4">
                    <Avatar className="h-14 w-14 border-2 border-muted">
                        <AvatarImage
                            src={(() => {
                                if (!farmer.passport_photo || !import.meta.env.VITE_FRAPPE_URL) return undefined;
                                try {
                                    return new URL(farmer.passport_photo, import.meta.env.VITE_FRAPPE_URL).toString();
                                } catch (e) {
                                    console.error("Invalid URL:", e);
                                    return undefined;
                                }
                            })()}
                            alt={`${farmer.first_name} ${farmer.last_name}`}
                        />
                        <AvatarFallback className="text-lg font-medium bg-primary/10 text-primary">
                            {(farmer.first_name?.[0] || '?')}{(farmer.last_name?.[0] || '?')}
                        </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col">
                        <CardTitle className="text-base font-semibold">
                            {farmer.first_name} {farmer.last_name}
                        </CardTitle>
                        <div className="flex items-center gap-1 mt-0.5">
                            <PhoneIcon className="h-3 w-3 text-muted-foreground" aria-hidden="true" />
                            <CardDescription className="text-xs">{farmer.phone_number}</CardDescription>
                        </div>
                        <div className="flex items-center gap-1 mt-0.5">
                            <MapPinIcon className="h-3 w-3 text-muted-foreground" />
                            <CardDescription className="text-xs">{farmer.cluster_name}</CardDescription>
                        </div>
                        <div className="flex items-center gap-1 mt-0.5">
                            <Map className="h-3 w-3 text-muted-foreground" />
                            <CardDescription className="text-xs">{farmer.size_in_hectares.toFixed(2)} ha</CardDescription>
                        </div>
                        <div className="flex items-center gap-1 mt-0.5">
                            <Trees className="h-3 w-3 text-muted-foreground" />
                            <CardDescription className="text-xs">{farmer.commodity}</CardDescription>
                        </div>
                    </div>
                </div>
            </CardHeader>

            <CardFooter className="pt-0 border-t text-xs text-muted-foreground">
                <div className="flex items-center gap-1.5 w-full justify-end">
                    <CalendarIcon className="h-3.5 w-3.5" />
                    <span>Created {formattedDate}</span>
                </div>
            </CardFooter>
        </Card>
    )
}